import React, { useMemo } from "react";
import { connect } from "react-redux";

import FormItem from "antd/es/form/FormItem";
import Select from "antd/es/select";
import { useIntl } from "react-intl";

import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import { SelectField } from "@mapmycustomers/ui";

import { getAllColorLegends, getAllShapeLegends } from "@app/store/pinLegends";
import { RootState } from "@app/store/rootReducer";
import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";
import { isSingleShapePinLegend } from "@app/util/pinLegend";
import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./DefaultKeysItem.module.scss";

interface Props {
  colorLegends: PinLegend[];
  entityType: EntityTypesSupportedByMapsPage;
  shapeLegends: PinLegend[];
}

const DefaultKeysItem: React.FC<Props> = ({ colorLegends, entityType, shapeLegends }) => {
  const intl = useIntl();

  const colorOptions = useMemo(
    () => colorLegends.filter(({ entity }) => entity === entityType).map(defaultSelectOptionMapper),
    [colorLegends, entityType]
  );

  const { otherShapeLegends, singleShapeLegends } = useMemo(() => {
    const otherOptions: PinLegend[] = [];
    const singleShapeOptions: PinLegend[] = [];
    shapeLegends
      .filter(({ entity }) => entity === entityType)
      .forEach((legend) =>
        (isSingleShapePinLegend(legend) ? singleShapeOptions : otherOptions).push(legend)
      );
    return { otherShapeLegends: otherOptions, singleShapeLegends: singleShapeOptions };
  }, [entityType, shapeLegends]);

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        {getEntityTypeDisplayName(intl, entityType, { lowercase: false, plural: true })}
      </div>
      <div className={styles.fields}>
        <FormItem
          label={intl.formatMessage({
            id: "settings.organizationDetails.map.defaultKeys.colorSelect",
            defaultMessage: "Color Legend",
            description: "Title of Color Select field Org Map settings",
          })}
          name={["defaultColorKey", entityType]}
        >
          <SelectField<PinLegend["id"]> className={styles.selectField} options={colorOptions} />
        </FormItem>
        <FormItem
          label={intl.formatMessage({
            id: "settings.organizationDetails.map.defaultKeys.shapeSelect",
            defaultMessage: "Shape Legend",
            description: "Title of Shape Select field Org Map settings",
          })}
          name={["defaultShapeKey", entityType]}
        >
          <SelectField<PinLegend["id"]> className={styles.selectField}>
            {otherShapeLegends.map((legend) => (
              <Select.Option key={legend.id} value={legend.id}>
                {legend.name}
              </Select.Option>
            ))}
            {singleShapeLegends.length && (
              <Select.OptGroup
                label={intl.formatMessage({
                  id: "settings.organizationDetails.map.defaultKeys.shapeSelect.singleShapesGroup",
                  defaultMessage: "Single Shapes",
                  description:
                    "Title of Single Shapes group of options in Shape Select fix in Org Map settings",
                })}
              >
                {singleShapeLegends.map((legend) => (
                  <Select.Option key={legend.id} value={legend.id}>
                    {legend.name}
                  </Select.Option>
                ))}
              </Select.OptGroup>
            )}
          </SelectField>
        </FormItem>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  colorLegends: getAllColorLegends(state),
  shapeLegends: getAllShapeLegends(state),
});

export default connect(mapStateToProps)(DefaultKeysItem);
